import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { expensesAPI } from '../services/api';
import { tablePageHeaderStickySx, tablePageRootSx } from '../theme/tablePageLayout';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0.00';
  }
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ExpenseSummaryReportPage = () => {
  const [expenseSummary, setExpenseSummary] = useState({ years: [], categories: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadExpenseSummary = async () => {
      try {
        const response = await expensesAPI.getSummaryByYear();
        setExpenseSummary(response.data ?? { years: [], categories: [], totals: {} });
      } catch (err) {
        console.error('Error loading expense summary:', err);
        setError('Could not load the expense summary.');
      } finally {
        setLoading(false);
      }
    };
    loadExpenseSummary();
  }, []);

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Typography variant="h4" sx={{ pt: 2, pb: 1 }}>
          Expense Summary
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Year-over-year expense totals by category.
        </Typography>

        <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: 960 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                {expenseSummary.years.map((year) => (
                  <TableCell key={year} align="right">{year}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={2} />}
              {expenseSummary.categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell>{cat.name}</TableCell>
                  {expenseSummary.years.map((year) => (
                    <TableCell key={year} align="right">
                      ${formatCurrency(cat.totals[String(year)])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                {expenseSummary.years.map((year) => (
                  <TableCell key={year} align="right" sx={{ fontWeight: 'bold' }}>
                    ${formatCurrency(expenseSummary.totals[String(year)])}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ExpenseSummaryReportPage;
